// M13 Notification Bell — top navigation. Unread badge, drawer with recent
// notifications, mark read / mark all read, deep-link to originating record.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Bell, CheckCheck, Inbox, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { api, getErrorMessage } from "@/lib/api";
import { formatDateTime } from "@/lib/constants";
import { resolveNotificationLink } from "@/lib/notificationLinks";
import { useAuth } from "@/context/AuthContext";

const PRIORITY_DOT = {
  Critical: "bg-red-500",
  High: "bg-amber-500",
  Normal: "bg-primary",
  Low: "bg-slate-400",
};

export const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    api.get("/notifications/summary")
      .then((res) => setSummary(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (open) {
      setLoading(true);
      api.get("/notifications/summary")
        .then((res) => setSummary(res.data))
        .catch((e) => toast.error(getErrorMessage(e)))
        .finally(() => setLoading(false));
    }
  }, [open]);

  const unread = summary?.unread || 0;

  const openItem = async (n) => {
    try {
      if (n.status === "Unread") await api.put(`/notifications/${n.id}/read`);
    } catch {
      /* non-blocking */
    }
    load();
    setOpen(false);
    const link = resolveNotificationLink(n, user.role);
    navigate(link || "/notifications");
  };

  const markAllRead = async () => {
    try {
      const res = await api.put("/notifications/mark-all-read");
      toast.success(`${res.data.count} notification(s) marked as read`);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-lg" data-testid="notification-bell"
                aria-label="Notifications">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              data-testid="notification-bell-count"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="notification-drawer">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-border">
          <div className="flex items-center justify-between gap-2">
            <SheetTitle className="flex items-center gap-2 text-base">
              <Bell className="h-4 w-4 text-primary" /> Notifications
              {unread > 0 && (
                <Badge variant="secondary" className="rounded-full" data-testid="drawer-unread-badge">
                  {unread} unread
                </Badge>
              )}
            </SheetTitle>
            <Button variant="ghost" size="sm" className="gap-1.5 text-xs" onClick={markAllRead}
                    disabled={unread === 0} data-testid="drawer-mark-all-read">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </Button>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {loading && !summary ? (
            <div className="p-5 space-y-3">
              {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
            </div>
          ) : !summary || summary.recent.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-6" data-testid="drawer-empty-state">
              <Inbox className="h-10 w-10 text-muted-foreground/40 mb-3" />
              <p className="text-sm font-medium">You're all caught up</p>
              <p className="text-xs text-muted-foreground mt-1">New business events will appear here.</p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {summary.recent.map((n) => (
                <li key={n.id}>
                  <button
                    onClick={() => openItem(n)}
                    data-testid={`drawer-notification-${n.notify_code}`}
                    className={`w-full text-left px-5 py-3.5 hover:bg-muted/60 transition-colors focus-visible:outline-none focus-visible:bg-muted ${n.status === "Unread" ? "bg-primary/[0.04]" : ""}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${n.status === "Unread" ? PRIORITY_DOT[n.priority] || PRIORITY_DOT.Normal : "bg-transparent border border-border"}`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          {n.notification_type === "Admin Announcement" && <Megaphone className="h-3.5 w-3.5 text-primary shrink-0" />}
                          {n.priority === "Critical" && <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />}
                          <p className={`text-sm truncate ${n.status === "Unread" ? "font-semibold" : "font-medium text-muted-foreground"}`}>
                            {n.title}
                          </p>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.message}</p>
                        <p className="text-[11px] text-muted-foreground/70 mt-1 font-mono">
                          {n.notify_code} · {formatDateTime(n.last_merged_at || n.created_at)}
                        </p>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-border p-3">
          <Button variant="outline" className="w-full" data-testid="drawer-view-all"
                  onClick={() => { setOpen(false); navigate("/notifications"); }}>
            View all notifications
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
